import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, MessageCircle } from 'lucide-react';
import { navigation, siteConfig } from '../mock/data';
import { trackClick } from '../lib/tracking';
import { useSettings, useLogoHeader } from '../context/SettingsContext';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [logoFailed, setLogoFailed] = useState(false);
  const location = useLocation();
  const { settings } = useSettings();
  const { logo: logoHeader, loading: logoLoading } = useLogoHeader();
  const logoSrc = logoHeader
    ? `${logoHeader}${logoHeader.includes('?') ? '&' : '?'}v=${encodeURIComponent(settings.updated_at || '')}`
    : '';

  useEffect(() => {
    setLogoFailed(false);
  }, [logoSrc]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenSubmenu(null);
  }, [location.pathname]);

  const waLink = `https://api.whatsapp.com/send?phone=${settings.whatsapp_number || siteConfig.whatsappNumber}&text=${encodeURIComponent(siteConfig.waMessage)}`;
  const brandName = settings.brand_name || 'BelBel';
  const brandFull = settings.brand_full || 'Sidoarjo Powder';
  const navItems = Array.isArray(settings.navigation) ? settings.navigation : navigation;
  const showLogo = logoSrc && !logoFailed;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-md py-2' : 'bg-white py-3'
      }`}
    >
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group min-h-[44px]">
            {showLogo ? (
              <img
                src={logoSrc}
                alt={brandName}
                width={160}
                height={44}
                decoding="async"
                fetchPriority="high"
                className="h-11 w-auto max-w-[160px] object-contain"
                onError={() => setLogoFailed(true)}
              />
            ) : logoLoading ? (
              <div className="h-11 w-28 rounded-lg bg-gray-100 animate-pulse" aria-hidden />
            ) : (
              <>
                <div className="w-11 h-11 rounded-xl bg-japo-green flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow">
                  <span className="text-white font-extrabold text-xl tracking-tight">{brandName[0]}</span>
                </div>
                <div className="leading-tight">
                  <div className="font-extrabold text-lg text-japo-green tracking-tight">{brandName}</div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">{brandFull}</div>
                </div>
              </>
            )}
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <div key={item.label} className="relative group">
                <NavLink
                  to={item.path}
                  end={item.path === '/'}
                  className={({ isActive }) =>
                    `px-4 py-2 rounded-lg text-[15px] font-semibold transition-colors flex items-center gap-1 ${
                      isActive ? 'text-japo-green bg-rose-50' : 'text-gray-700 hover:text-japo-green hover:bg-rose-50/60'
                    }`
                  }
                >
                  {item.label}
                  {item.submenu && <ChevronDown className="w-4 h-4 opacity-70 group-hover:rotate-180 transition-transform" />}
                </NavLink>
                {item.submenu && (
                  <div className="absolute top-full left-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="bg-white rounded-xl shadow-xl border border-gray-100 py-2 min-w-[220px]">
                      {item.submenu.map((sub) => (
                        <Link
                          key={sub.label}
                          to={sub.path}
                          className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-rose-50 hover:text-japo-green font-medium transition-colors"
                        >
                          {sub.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackClick('whatsapp_header')}
              className="hidden md:inline-flex items-center gap-2 bg-japo-yellow hover:bg-[#D19400] text-gray-900 font-bold px-5 py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all"
            >
              <MessageCircle className="w-4 h-4" />
              Pesan Sekarang
            </a>
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileOpen && (
          <nav className="lg:hidden mt-4 pb-4 border-t border-gray-100 pt-3">
            {navItems.map((item) => (
              <div key={item.label} className="border-b border-gray-50 last:border-0">
                <div className="flex items-center justify-between">
                  <NavLink
                    to={item.path}
                    end={item.path === '/'}
                    className={({ isActive }) =>
                      `flex-1 py-3 px-2 font-semibold ${
                        isActive ? 'text-japo-green' : 'text-gray-700'
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                  {item.submenu && (
                    <button
                      onClick={() => setOpenSubmenu(openSubmenu === item.label ? null : item.label)}
                      className="p-2"
                    >
                      <ChevronDown className={`w-4 h-4 transition-transform ${openSubmenu === item.label ? 'rotate-180' : ''}`} />
                    </button>
                  )}
                </div>
                {item.submenu && openSubmenu === item.label && (
                  <div className="pl-4 pb-2 space-y-1">
                    {item.submenu.map((sub) => (
                      <Link
                        key={sub.label}
                        to={sub.path}
                        className="block py-2 px-2 text-sm text-gray-600 hover:text-japo-green"
                      >
                        {sub.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackClick('whatsapp_header_mobile')}
              className="mt-4 flex items-center justify-center gap-2 bg-japo-yellow text-gray-900 font-bold px-4 py-3 rounded-lg"
            >
              <MessageCircle className="w-4 h-4" />
              Pesan Sekarang via WhatsApp
            </a>
          </nav>
        )}
      </div>
    </header>
  );
}
